import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Plus, CreditCard, InfoIcon, Loader2, CheckCircle } from "lucide-react";
import { OnboardingData } from "../index";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

interface ConnectAccountsProps {
  onNext: () => void;
  onPrevious: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

export default function ConnectAccounts({
  onNext,
  onPrevious,
  onboardingData,
  updateOnboardingData,
}: ConnectAccountsProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [skipBankConnection, setSkipBankConnection] = useState(false);
  const [connectedAccounts, setConnectedAccounts] = useState<Array<{
    id: string;
    name: string;
    type: string;
    balance: string;
  }>>(onboardingData.connectedAccounts || []);
  
  // Mock function to simulate connecting to a bank account
  const handleConnectBank = async () => {
    setIsLoading(true);
    
    // Simulate bank connection process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Add sample bank account
    const newAccount = {
      id: `acc-${Date.now()}`,
      name: "Sample Bank Account",
      type: "Checking",
      balance: "$5,243.00",
    };
    
    const updatedAccounts = [...connectedAccounts, newAccount];
    setConnectedAccounts(updatedAccounts);
    
    // Update onboarding data
    updateOnboardingData({
      connectedAccounts: updatedAccounts,
      bankConnected: true,
    });
    
    toast({
      title: "Account connected",
      description: "Your bank account has been connected successfully.",
    });
    
    setIsLoading(false);
  };
  
  const handleContinue = async () => {
    try {
      setIsLoading(true);
      
      // If user chooses to skip bank connection
      if (skipBankConnection) {
        updateOnboardingData({
          bankConnected: false,
        });
        
        toast({
          title: "Bank connection skipped",
          description: "You can connect your bank accounts later in settings.",
        });
      }
      
      // Proceed to next step
      onNext();
    } catch (error) {
      toast({
        title: "Error",
        description: "There was a problem saving your preferences. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold">Connect Your Bank Accounts</h2>
          <p className="mt-2 text-neutral-600">
            Link your bank accounts to automatically import transactions
          </p>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-medium">Connected Accounts</h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <InfoIcon className="h-4 w-4 text-neutral-400" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">Connect your bank accounts to automatically import transactions into ZentroBooks</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex items-center"
                      disabled={isLoading}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Connect Account
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Connect Bank Account</DialogTitle>
                      <DialogDescription>
                        Link your bank account to automatically import transactions into ZentroBooks.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center space-x-2 py-4">
                      <div className="grid flex-1 gap-2">
                        <p className="text-sm text-neutral-600">Your financial data is encrypted and secure. ZentroBooks uses the same security measures as major banks.</p>
                        <div className="bg-primary/5 p-4 rounded-md">
                          <h4 className="font-medium text-sm mb-2">Demo Credentials</h4>
                          <p className="text-xs text-neutral-600">
                            Username: user_good<br />
                            Password: pass_good
                          </p>
                        </div>
                      </div>
                    </div>
                    <DialogFooter className="sm:justify-end">
                      <DialogClose asChild>
                        <Button variant="outline">Cancel</Button>
                      </DialogClose>
                      <Button 
                        type="button" 
                        onClick={handleConnectBank}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Connecting...
                          </>
                        ) : (
                          "Connect"
                        )}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              
              {connectedAccounts.length > 0 ? (
                <div className="space-y-3">
                  {connectedAccounts.map(account => (
                    <div 
                      key={account.id}
                      className="flex justify-between items-center p-4 border rounded-md"
                    >
                      <div className="flex items-center">
                        <div className="bg-primary/10 p-2 rounded-full mr-3">
                          <CreditCard className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">{account.name}</h4>
                          <p className="text-sm text-neutral-500">{account.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{account.balance}</p>
                        <div className="flex items-center text-xs text-green-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Connected
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="border border-dashed rounded-md p-8 text-center">
                  <CreditCard className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
                  <h3 className="text-base font-medium mb-1">No accounts connected</h3>
                  <p className="text-sm text-neutral-500 mb-4">Connect your bank accounts to automatically import transactions</p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <Plus className="mr-2 h-4 w-4" />
                        Connect Account
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Connect Bank Account</DialogTitle>
                        <DialogDescription>
                          Link your bank account to automatically import transactions into ZentroBooks.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex items-center space-x-2 py-4">
                        <div className="grid flex-1 gap-2">
                          <p className="text-sm text-neutral-600">Your financial data is encrypted and secure. ZentroBooks uses the same security measures as major banks.</p>
                          <div className="bg-primary/5 p-4 rounded-md">
                            <h4 className="font-medium text-sm mb-2">Demo Credentials</h4>
                            <p className="text-xs text-neutral-600">
                              Username: user_good<br />
                              Password: pass_good
                            </p>
                          </div>
                        </div>
                      </div>
                      <DialogFooter className="sm:justify-end">
                        <DialogClose asChild>
                          <Button variant="outline">Cancel</Button>
                        </DialogClose>
                        <Button 
                          type="button" 
                          onClick={handleConnectBank}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Connecting...
                            </>
                          ) : (
                            "Connect"
                          )}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              
              <div className="mt-6 flex items-center space-x-2">
                <Switch
                  id="skip-connection"
                  checked={skipBankConnection}
                  onCheckedChange={setSkipBankConnection}
                />
                <Label htmlFor="skip-connection">I'll connect my accounts later</Label>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <div className="flex justify-between pt-4">
          <Button type="button" variant="outline" onClick={onPrevious}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          
          <Button 
            type="button" 
            onClick={handleContinue}
            disabled={isLoading && !skipBankConnection && connectedAccounts.length === 0}
          >
            {isLoading ? (
              "Saving..."
            ) : (
              <>
                Continue
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}